import { useEffect, useRef } from 'react';
import { type MediaHandlerProps, useMediaUtils } from '..';

type MixCloudEventData = {
  type: string;
};

type WidgetEvent<T = () => void> = {
  on: (callback: T) => void;
};

type Widget = {
  ready: Promise<void>;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (seconds: number) => void;
  getPosition?: () => Promise<number>;
  getDuration?: () => Promise<number>;
  getIsPaused?: () => Promise<boolean>;
  events: {
    progress: WidgetEvent<(position: number, duration: number) => void>;
    buffering: WidgetEvent;
    play: WidgetEvent;
    pause: WidgetEvent;
    ended: WidgetEvent;
    error: WidgetEvent;
  };
};

export const useMixcloudHandler = ({ eventName }: MediaHandlerProps) => {
  const isWidgetLoaded = useRef(false);
  const { isFirstPlay, push, getIframe, getPercentage, playbackDuration, isScriptLoaded, loadScript, checkSrcType } =
    useMediaUtils(eventName);

  const initWidget = () => {
    if (isWidgetLoaded.current) return;
    if (!window.Mixcloud) return;

    const iframe = getIframe();
    if (!iframe) return;

    const srcType = checkSrcType(iframe.src);
    if (srcType !== 'mixcloud') return;

    isWidgetLoaded.current = true;

    // create the widget
    const widget = window.Mixcloud.PlayerWidget(iframe) as Widget;

    const updateDuration = () => {
      if (playbackDuration.current !== 0) return;

      widget?.getDuration?.()?.then((duration: number) => {
        playbackDuration.current = duration;
      });
    };

    widget.ready.then(() => {
      updateDuration();

      widget.events.play.on(() => {
        if (isFirstPlay.current) return;
        isFirstPlay.current = true;

        if (!playbackDuration.current) updateDuration();
        push('play');
      });

      widget.events.progress.on((pos) => {
        // check that we have duration
        if (playbackDuration.current <= 0) {
          updateDuration();
          return;
        }

        const percentage = getPercentage(pos, playbackDuration.current);
        if (!!percentage) push(percentage);
      });
    });
  };

  useEffect(() => {
    const listener = (event: MessageEvent<string>) => {
      if (!event.origin.includes('.mixcloud.com')) return;

      try {
        const data = JSON.parse(event.data) as MixCloudEventData;

        if (data?.type === 'ready') {
          if (isScriptLoaded.current) initWidget();
          else
            loadScript('https://widget.mixcloud.com/media/js/widgetApi.js', () => {
              initWidget();
            });
        }
      } catch (error) {}
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);
};
